import React, { useState, useEffect, useMemo } from "react";
import { Form, Button, Alert, Row, Col, InputGroup, Spinner } from "react-bootstrap";
import API from "../services/api";
import "./RequirementForm.css";
import { formatVendorId } from "../utils/idFormat";

const RequirementForm = () => {
  const [formData, setFormData] = useState({
    productType: "",
    quantity: "",
    unit: "KG",
    requirementAmount: "",
    notes: "",
    // we'll send `location` as an object with `address`
    location: { address: "" },
  });

  const [companyName, setCompanyName] = useState("");
  const [vendorCode, setVendorCode] = useState("");
  const [financialSummary, setFinancialSummary] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  // ---------- Product selectors ----------
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState("");
  const [selectedProductId, setSelectedProductId] = useState("");
  const [useCustomProduct, setUseCustomProduct] = useState(false);
  const [requirementItems, setRequirementItems] = useState([]);
  const [isAmountManual, setIsAmountManual] = useState(false);

  // ---------- Address / PIN ----------
  const [pincode, setPincode] = useState("");
  const [stateName, setStateName] = useState("");
  const [district, setDistrict] = useState("");
  const [areas, setAreas] = useState([]); // Post Office names
  const [area, setArea] = useState("");
  const [addressLine, setAddressLine] = useState("");

  const selectedProduct = useMemo(
    () => catalogProducts.find((p) => p._id === selectedProductId) || null,
    [catalogProducts, selectedProductId]
  );
  const selectedProductWeight = useMemo(() => {
    if (!selectedProduct) return "";
    const v = selectedProduct.weightValue;
    if (v === undefined || v === null || v === "") return "";
    return `${v} ${selectedProduct.weightUnit || "KG"}`;
  }, [selectedProduct]);
  const selectedProductLabel = useMemo(() => {
    if (!selectedProduct) return "";
    return selectedProductWeight ? `${selectedProduct.name} (${selectedProductWeight})` : selectedProduct.name || "";
  }, [selectedProduct, selectedProductWeight]);

  const selectedPricingTier = useMemo(() => {
    if (!selectedProduct || !selectedProduct.pricingTiers?.length) return null;
    const qty = Number(formData.quantity || 0);
    if (!Number.isFinite(qty) || qty <= 0) return null;
    const tiers = [...selectedProduct.pricingTiers]
      .map((t) => ({
        minQty: Number(t.minQty ?? 0),
        maxQty: t.maxQty === null || t.maxQty === undefined || t.maxQty === "" ? null : Number(t.maxQty),
        rate: Number(t.rate || 0),
        discountPercent: Number(t.discountPercent || 0),
      }))
      .filter((t) => Number.isFinite(t.minQty) && Number.isFinite(t.rate) && t.rate > 0)
      .sort((a, b) => a.minQty - b.minQty);
    if (!tiers.length) return null;

    const exact = tiers.find((t) => (t.maxQty === null ? qty >= t.minQty : qty >= t.minQty && qty <= t.maxQty));
    return exact || [...tiers].reverse().find((t) => qty >= t.minQty) || tiers[0] || null;
  }, [selectedProduct, formData.quantity]);

  const estimatePricing = useMemo(() => {
    const qty = Number(formData.quantity || 0);
    const baseRate = Number(selectedPricingTier?.rate || 0);
    const discountPercent = Number(selectedPricingTier?.discountPercent || 0);
    const discountedRate =
      Number.isFinite(baseRate) && baseRate > 0
        ? Math.round((baseRate - (baseRate * discountPercent) / 100) * 100) / 100
        : 0;
    const subtotalAmount = Math.round((discountedRate * qty) * 100) / 100;
    const discountAmount = Math.max(
      0,
      Math.round(((baseRate * qty) - subtotalAmount) * 100) / 100
    );
    const shouldApplyGst =
      String(selectedProduct?.weightUnit || "KG").trim().toUpperCase() === "KG" &&
      Number.isFinite(Number(selectedProduct?.weightValue || 0)) &&
      Number(selectedProduct?.weightValue || 0) >= 1 &&
      Number(selectedProduct?.weightValue || 0) <= 25;
    const gstPercent = shouldApplyGst ? Number(selectedProduct?.gstPercent || 0) : 0;
    const gstAmount = Math.round(((subtotalAmount * gstPercent) / 100) * 100) / 100;
    const totalAmount = Math.round((subtotalAmount + gstAmount) * 100) / 100;
    return { baseRate, rate: discountedRate, subtotalAmount, discountPercent, discountAmount, gstPercent, gstAmount, totalAmount };
  }, [selectedPricingTier, selectedProduct, formData.quantity, formData.unit]);

  useEffect(() => {
    if (selectedProduct) {
      setFormData((prev) => ({
        ...prev,
        requirementAmount: estimatePricing.totalAmount > 0 ? String(estimatePricing.totalAmount) : "",
      }));
      return;
    }
    if (!isAmountManual && estimatePricing.totalAmount > 0) {
      setFormData((prev) => ({ ...prev, requirementAmount: String(estimatePricing.totalAmount) }));
    }
  }, [estimatePricing, isAmountManual]);

  // Fetch vendor profile for display
  useEffect(() => {
    const fetchVendorInfo = async () => {
      try {
        const res = await API.get("/vendor/profile");
        setCompanyName(res.data.companyName || "");
        setVendorCode(formatVendorId(res.data || {}, {}));
        setFinancialSummary(res.data?.financialSummary || null);
      } catch (err) {
        console.error(err);
        setCompanyName("");
        setFinancialSummary(null);
      }
    };
    fetchVendorInfo();
  }, []);

  const draftItemAmount = useMemo(() => {
    const hasDraft =
      String(formData.productType || "").trim() &&
      String(formData.quantity || "").trim();
    if (!hasDraft) return 0;
    if (selectedProduct && estimatePricing.totalAmount <= 0) {
      return { error: "Selected catalog product price could not be calculated. Please check product pricing tiers." };
    }
    const finalAmount = estimatePricing.totalAmount > 0 ? estimatePricing.totalAmount : Number(formData.requirementAmount || 0);
    if (!Number.isFinite(finalAmount) || finalAmount <= 0) return 0;
    return finalAmount;
  }, [formData.productType, formData.quantity, formData.requirementAmount, estimatePricing]);

  const selectedItemsTotal = useMemo(
    () => requirementItems.reduce((sum, item) => sum + Number(item?.requirementAmount || 0), 0),
    [requirementItems]
  );

  const estimatedTotalAmount = useMemo(
    () => Math.round((selectedItemsTotal + draftItemAmount + Number.EPSILON) * 100) / 100,
    [selectedItemsTotal, draftItemAmount]
  );

  const availableLimit = Number(financialSummary?.availableLimit || 0);
  const securityDepositRequired = Number(financialSummary?.securityDepositRequired || 0);
  const securityDepositPaid = !!financialSummary?.securityDepositPaid;
  const canPlaceRequirement = !!financialSummary?.canPlaceRequirement;
  const exceedsAvailableLimit = estimatedTotalAmount > availableLimit;

  // Fetch admin-defined products
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setCatalogLoading(true);
        const res = await API.get("/vendor/products");
        setCatalogProducts(res.data || []);
        setCatalogError("");
      } catch (err) {
        console.error("Error fetching products:", err);
        setCatalogError("Unable to load products right now.");
      } finally {
        setCatalogLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Sync selected product into formData
  useEffect(() => {
    if (useCustomProduct) return;
    const nextProduct = selectedProduct?.name || "";
    setFormData((prev) => ({
      ...prev,
      productType: nextProduct,
      unit: selectedProduct?.unit || prev.unit,
    }));
    setIsAmountManual(false);
  }, [selectedProduct, useCustomProduct]);

  // Auto-fetch address details from Indian PIN API
  // https://api.postalpincode.in/pincode/{pincode}
  const lookupPincode = async (pin) => {
    try {
      if (!pin || pin.length !== 6) return;
      const res = await fetch(`https://api.postalpincode.in/pincode/${pin}`);
      const data = await res.json();

      if (Array.isArray(data) && data[0]?.Status === "Success") {
        const poList = data[0].PostOffice || [];
        const districts = [...new Set(poList.map((po) => po.District).filter(Boolean))];
        const states = [...new Set(poList.map((po) => po.State).filter(Boolean))];

        setDistrict(districts[0] || "");
        setStateName(states[0] || "");
        setAreas(poList.map((po) => po.Name).filter(Boolean));
        // Default select first area for convenience
        setArea((prev) => (prev ? prev : poList[0]?.Name || ""));
      } else {
        setAreas([]);
        setArea("");
        setDistrict("");
        setStateName("");
      }
    } catch (e) {
      console.error("PIN lookup failed", e);
      setAreas([]);
      setArea("");
      setDistrict("");
      setStateName("");
    }
  };

  useEffect(() => {
    if (pincode.length === 6) lookupPincode(pincode);
  }, [pincode]);

  // Compose full address preview and persist into formData.location.address
  const fullAddress = useMemo(() => {
    const parts = [
      addressLine?.trim(),
      area?.trim(),
      district?.trim(),
      stateName?.trim(),
      pincode?.trim(),
    ].filter(Boolean);
    return parts.join(", ");
  }, [addressLine, area, district, stateName, pincode]);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      location: { address: fullAddress },
    }));
  }, [fullAddress]);

  // ---------- Change handlers ----------
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "requirementAmount") setIsAmountManual(true);
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const clearCurrentProductInput = () => {
    setFormData((prev) => ({
      ...prev,
      productType: "",
      quantity: "",
      unit: "KG",
      requirementAmount: "",
    }));
    setSelectedProductId("");
    setUseCustomProduct(false);
    setIsAmountManual(false);
  };

  const buildCurrentItem = () => {
    const effectiveProductType = formData.productType?.trim();
    if (!effectiveProductType) return { error: "Please select or enter a Product." };
    const qty = Number(formData.quantity || 0);
    if (!Number.isFinite(qty) || qty <= 0) return { error: "Please enter a valid quantity." };
    const finalAmount = estimatePricing.totalAmount > 0 ? estimatePricing.totalAmount : Number(formData.requirementAmount || 0);
    if (!Number.isFinite(finalAmount) || finalAmount <= 0) {
      return { error: "Please enter a valid requirement amount." };
    }
    if (availableLimit <= 0) {
      return { error: "Available limit is zero. You cannot submit a requirement right now." };
    }
    const nextTotal =
      Math.round((selectedItemsTotal + Number(finalAmount || 0) + Number.EPSILON) * 100) / 100;
    if (nextTotal > availableLimit) {
      return {
        error: `Requirement total cannot exceed available limit of ₹${availableLimit.toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}.`,
      };
    }
    return {
      item: {
        productId: selectedProduct?._id || undefined,
        productType: effectiveProductType,
        packLabel: selectedProductLabel || effectiveProductType,
        quantity: qty,
        unit: formData.unit,
        rate: estimatePricing.rate || 0,
        subtotalAmount: estimatePricing.subtotalAmount > 0 ? estimatePricing.subtotalAmount : finalAmount,
        discountPercent: estimatePricing.discountPercent > 0 ? estimatePricing.discountPercent : 0,
        discountAmount: estimatePricing.discountAmount > 0 ? estimatePricing.discountAmount : 0,
        gstPercent: estimatePricing.gstPercent > 0 ? estimatePricing.gstPercent : 0,
        gstAmount: estimatePricing.subtotalAmount > 0 ? estimatePricing.gstAmount : 0,
        totalAmount: finalAmount,
        requirementAmount: finalAmount,
      },
    };
  };

  const handleAddProductItem = () => {
    setMessage("");
    setError("");
    const { item, error: itemError } = buildCurrentItem();
    if (itemError) {
      setError(itemError);
      return;
    }
    setRequirementItems((prev) => [...prev, item]);
    clearCurrentProductInput();
  };

  const removeProductItem = (idx) => {
    setRequirementItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");

    if (!fullAddress) {
      setError("Please enter a valid address (pincode + details).");
      return;
    }

    const payloadItems = [...requirementItems];
    const hasUnaddedDraft =
      String(formData.productType || "").trim() ||
      String(formData.quantity || "").trim() ||
      String(formData.requirementAmount || "").trim();
    if (hasUnaddedDraft) {
      const { item, error: itemError } = buildCurrentItem();
      if (itemError) {
        setError(itemError);
        return;
      }
      payloadItems.push(item);
    }
    if (!payloadItems.length) {
      setError("Please add at least one product item.");
      return;
    }
    if (availableLimit <= 0) {
      setError("Available limit is zero. You cannot submit a requirement right now.");
      return;
    }
    const payloadTotal = payloadItems.reduce((sum, item) => sum + Number(item?.requirementAmount || 0), 0);
    if (payloadTotal > availableLimit) {
      setError(
        `Requirement total cannot exceed available limit of ₹${availableLimit.toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}.`
      );
      return;
    }

    try {
      // Backend currently accepts `location` as object or string.
      // We send { address: "..." }. If later you add lat/lng, include here.
      const payload = {
        items: payloadItems,
        notes: formData.notes,
        location: { address: fullAddress, pincode, city: district, state: stateName, area, line1: addressLine },
      };

      const res = await API.post("/vendor/submit-requirement", payload);
      setMessage(res.data.message || "Requirement submitted successfully!");

      // reset
      setFormData({
        productType: "",
        quantity: "",
        unit: "KG",
        requirementAmount: "",
        notes: "",
        location: { address: "" },
      });
      setSelectedProductId("");
      setUseCustomProduct(false);
      setRequirementItems([]);

      setPincode("");
      setStateName("");
      setDistrict("");
      setAreas([]);
      setArea("");
      setAddressLine("");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to submit requirement");
    }
  };

  return (
    <div className="req-page">
      

      <div className="req-header">
        <h2>Submit Product Requirement</h2>
        <p>Specify your rice supply needs, quantity, and delivery destination details below.</p>
      </div>

      {message && <Alert variant="success">{message}</Alert>}
      {error && <Alert variant="danger">{error}</Alert>}
      {!securityDepositPaid && securityDepositRequired > 0 && (
        <Alert variant="warning">
          Security deposit is pending. Please contact admin before placing requirements.
        </Alert>
      )}
      {securityDepositPaid && !canPlaceRequirement && (
        <Alert variant="warning">
          Available limit is zero. You cannot submit any requirement until admin increases your limit.
        </Alert>
      )}
      {canPlaceRequirement && exceedsAvailableLimit && (
        <Alert variant="warning">
          Selected requirement amount exceeds your available limit of ₹
          {availableLimit.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}.
        </Alert>
      )}

      <form className="req-form" onSubmit={handleSubmit}>
        {/* Section 1 */}
        <section className="req-card">
          <div className="req-card-title">
            <span className="req-icon">badge</span>
            <h3>1. Vendor Information</h3>
          </div>
          <div className="req-grid-2">
            <div className="req-field">
              <label>Vendor Name</label>
              <input value={companyName} readOnly className="req-input disabled" />
            </div>
            <div className="req-field">
              <label>Vendor ID</label>
              <input value={vendorCode || ""} readOnly className="req-input disabled" />
            </div>
          </div>
        </section>

        {/* Section 2 */}
        <section className="req-card">
          <div className="req-card-title between">
            <div className="req-title-left">
              <span className="req-icon">shopping_basket</span>
              <h3>2. Product Selection</h3>
            </div>
            <div className="req-switch">
              <span>Custom Product</span>
              <Form.Check
                type="switch"
                id="customSwitch"
                checked={useCustomProduct}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setUseCustomProduct(checked);
                  if (checked) {
                    setSelectedProductId("");
                    setFormData((prev) => ({ ...prev, productType: "" }));
                  }
                }}
              />
            </div>
          </div>

          <Row className="g-4">
            <Col lg={6}>
              <div className="req-field">
                <label>Select Catalog Product</label>
                <Form.Select
                  className="req-input"
                  value={selectedProductId}
                  onChange={(e) => setSelectedProductId(e.target.value)}
                  disabled={catalogLoading}
                >
                  <option value="">Select product</option>
                  {catalogProducts.map((p) => (
                    <option key={p._id} value={p._id}>
                      {p.name}
                      {p.weightValue !== undefined && p.weightValue !== null && p.weightValue !== ""
                        ? ` (${p.weightValue} ${p.weightUnit || "KG"})`
                        : ""}
                    </option>
                  ))}
                </Form.Select>
              </div>
              {!useCustomProduct && selectedProductWeight ? (
                <div className="req-muted mt-2">Selected Weight: {selectedProductWeight}</div>
              ) : null}
              {useCustomProduct && (
                <div className="req-field mt-3">
                  <label>Type Product Name</label>
                  <input
                    className="req-input"
                    type="text"
                    name="productType"
                    placeholder="e.g. Basmati Rice (1121)"
                    value={formData.productType}
                    onChange={handleChange}
                  />
                </div>
              )}

              <div className="req-field mt-3">
                <label>Quantity</label>
                <div className="req-qty">
                  <input
                    className="req-input"
                    type="number"
                    name="quantity"
                    min="1"
                    placeholder="Enter amount"
                    value={formData.quantity}
                    onChange={handleChange}
                    required
                  />
                  <Form.Select
                    className="req-input small"
                    name="unit"
                    value={formData.unit}
                    onChange={handleChange}
                    required
                    disabled={!useCustomProduct && !!selectedProduct}
                  >
                    <option value="KG">KG</option>
                    <option value="Ton">Ton</option>
                    <option value="Quintal">Quintal</option>
                    <option value="Litre">Litre</option>
                  </Form.Select>
                </div>
              </div>

              <div className="req-field mt-3">
                <label>Requirement Amount (INR)</label>
                <input
                  className="req-input"
                  type="number"
                  min="0"
                  max={availableLimit > 0 ? availableLimit : undefined}
                  step="0.01"
                  name="requirementAmount"
                  placeholder="Auto from pricing or enter manually"
                  value={formData.requirementAmount}
                  onChange={handleChange}
                />
                <div className="req-muted mt-1 d-flex align-items-center justify-content-between">
                  <span>{isAmountManual ? "Manual amount active" : "Auto amount from pricing tiers"}</span>
                  {estimatePricing.totalAmount > 0 ? (
                    <button
                      type="button"
                      className="req-link-btn"
                      onClick={() => {
                        setIsAmountManual(false);
                        setFormData((prev) => ({ ...prev, requirementAmount: String(estimatePricing.totalAmount) }));
                      }}
                    >
                      Use Auto Price
                    </button>
                  ) : null}
                </div>
              </div>
              <div className="mt-3 d-flex align-items-center gap-2">
                <Button
                  type="button"
                  className="req-btn"
                  onClick={handleAddProductItem}
                  disabled={!canPlaceRequirement || exceedsAvailableLimit}
                >
                  Add Product
                </Button>
                <span className="req-muted">Added: {requirementItems.length}</span>
              </div>

              {requirementItems.length > 0 ? (
                <div className="req-items mt-3">
                  {requirementItems.map((item, idx) => (
                    <div key={`${item.productType}_${idx}`} className="req-item-row">
                      <div>
                        <strong>{item.productType}</strong>
                        {item.packLabel && item.packLabel !== item.productType ? (
                          <div className="req-muted small">{item.packLabel}</div>
                        ) : null}
                        <div className="req-muted small">
                          {item.quantity} {item.unit}
                          {Number(item.rate || 0) > 0 ? ` | Rate ₹ ${Number(item.rate || 0).toLocaleString("en-IN")}/${item.unit || "unit"}` : ""}
                          {Number(item.discountAmount || 0) > 0
                            ? ` | Discount ₹ ${Number(item.discountAmount || 0).toLocaleString("en-IN")}`
                            : ""}
                          {Number(item.gstAmount || 0) > 0
                            ? ` | GST ₹ ${Number(item.gstAmount || 0).toLocaleString("en-IN")}`
                            : ""}
                          {" | "}
                          Final Amount ₹ {Number(item.requirementAmount || 0).toLocaleString("en-IN")}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="req-item-remove"
                        onClick={() => removeProductItem(idx)}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}

              {catalogError && <Alert variant="warning" className="mt-3 mb-0">{catalogError}</Alert>}
              {!catalogLoading && catalogProducts.length === 0 && !catalogError && (
                <Alert variant="info" className="mt-3 mb-0">
                  No products available. Please contact admin to add products.
                </Alert>
              )}
            </Col>

            <Col lg={6}>
              <div className="req-pricing">
                <h4>Volume-Based Pricing</h4>
                {catalogLoading ? (
                  <div className="req-loading">
                    <Spinner animation="border" size="sm" /> Loading pricing...
                  </div>
                ) : selectedProduct && selectedProduct.pricingTiers?.length ? (
                  <>
                    {selectedProductWeight ? (
                      <div className="req-muted mb-2">Product Weight: {selectedProductWeight}</div>
                    ) : null}
                    {selectedProduct.pricingTiers.map((tier, idx) => {
                      const min = tier.minQty ?? 0;
                      const max =
                        tier.maxQty === null || tier.maxQty === undefined
                          ? null
                          : tier.maxQty;
                      const range = max === null ? `${min}+` : `${min}-${max}`;
                      return (
                        <div key={`tier-${idx}`} className="req-price-row">
                          <span>{range} {selectedProduct.unit}</span>
                          <strong>Rs {tier.rate}</strong>
                        </div>
                      );
                    })}
                  </>
                ) : (
                  <div className="req-muted">No pricing tiers available.</div>
                )}
                <div className="req-price-foot">
                  <div className="req-price-label">Estimated Current Item</div>
                  <div className="req-price-value">
                    ₹ {estimatePricing.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                {estimatePricing.subtotalAmount > 0 ? (
                  <div className="req-muted mt-2">
                    Base ₹ {estimatePricing.subtotalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    {estimatePricing.discountPercent > 0 ? ` after ${estimatePricing.discountPercent}% discount (₹ ${estimatePricing.discountAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})` : ""}
                    {estimatePricing.gstPercent > 0 ? ` + GST ${estimatePricing.gstPercent}% (₹ ${estimatePricing.gstAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})` : " | No GST"}
                  </div>
                ) : null}
                {estimatePricing.totalAmount <= 0 && Number(formData.requirementAmount || 0) > 0 ? (
                  <div className="req-muted mt-2">
                    Manual amount: ₹ {Number(formData.requirementAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                ) : null}
                  <div className="req-price-label mt-3">Estimated Total (All Selected Products)</div>
                  <div className="req-price-value">
                    ₹ {estimatedTotalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <div className="req-muted mt-2">
                    Available Limit: ₹ {Number(financialSummary?.availableLimit || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </section>

        {/* Section 3 */}
        <section className="req-card">
          <div className="req-card-title">
            <span className="req-icon">local_shipping</span>
            <h3>3. Delivery Location</h3>
          </div>
          <Row className="g-3">
            <Col md={4}>
              <div className="req-field">
                <label>Pincode / Zip Code</label>
                <InputGroup>
                  <Form.Control
                    className="req-input"
                    type="text"
                    maxLength={6}
                    value={pincode}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, "");
                      setPincode(v);
                    }}
                    placeholder="6-digit code"
                  />
                  <Button
                    className="req-btn"
                    onClick={() => lookupPincode(pincode)}
                    disabled={pincode.length !== 6}
                  >
                    Lookup
                  </Button>
                </InputGroup>
              </div>
            </Col>
            <Col md={4}>
              <div className="req-field">
                <label>State</label>
                <input className="req-input muted" value={stateName} onChange={(e) => setStateName(e.target.value)} placeholder="Auto-populated" />
              </div>
            </Col>
            <Col md={4}>
              <div className="req-field">
                <label>City</label>
                <input className="req-input muted" value={district} onChange={(e) => setDistrict(e.target.value)} placeholder="Auto-populated" />
              </div>
            </Col>
            <Col md={6}>
              <div className="req-field">
                <label>Area / Locality</label>
                <Form.Select className="req-input" value={area} onChange={(e) => setArea(e.target.value)}>
                  <option value="">{areas.length ? "Select area" : "—"}</option>
                  {areas.map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </Form.Select>
              </div>
            </Col>
            <Col md={6}>
              <div className="req-field">
                <label>Full Address Line</label>
                <textarea
                  className="req-input"
                  rows="1"
                  value={addressLine}
                  onChange={(e) => setAddressLine(e.target.value)}
                  placeholder="Building No, Street, Landmark"
                />
              </div>
            </Col>
          </Row>
          <div className="req-preview">
            <div className="req-preview-label">Full Address Preview</div>
            <div className="req-preview-text">
              {fullAddress || "Please enter a valid pincode and address to see the preview here..."}
            </div>
          </div>
        </section>

        {/* Section 4 */}
        <section className="req-card">
          <div className="req-card-title">
            <span className="req-icon">description</span>
            <h3>4. Additional Notes</h3>
          </div>
          <textarea
            className="req-input"
            rows="4"
            name="notes"
            placeholder="Mention any quality certificates, packaging preferences, or delivery windows..."
            value={formData.notes}
            onChange={handleChange}
          />
        </section>

        {/* Fixed Actions */}
        <div className="req-actions">
          <div className="req-actions-inner">
            <div className="req-action-note">Saved as draft: 2:45 PM</div>
            <div className="req-action-buttons">
              <Button type="button" className="req-btn-ghost" onClick={() => window.history.back()}>
                Discard
              </Button>
              <Button type="submit" className="req-btn-primary" disabled={!canPlaceRequirement || exceedsAvailableLimit}>
                Submit Requirement
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default RequirementForm;
